import React from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';

import {
  Dre,
  IContaDoPlano,
  ICruzamentos,
  IDemonstracoes,
  useFechamentoSecao,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { COR, mesLongo, pct } from '../regras';
import { montarCarga } from '../regrasDaCarga';
import { num, numCurto, textoDaContaOu } from '../regrasDoDetalhe';
import { Indicador } from './comuns';

function Conteudo({
  dre,
  demonstracoes,
  cruzamentos,
  plano,
}: {
  dre: { mes: Dre; ano: Dre };
  demonstracoes: IDemonstracoes;
  cruzamentos: ICruzamentos;
  plano?: Record<string, IContaDoPlano>;
}) {
  const carga = montarCarga(dre, demonstracoes.dre_contas, cruzamentos.cruzamentos);
  const peso = (v: number) => (carga.receitaMes ? pct(Math.abs(v) / carga.receitaMes) : '—');
  return (
    <>
      <Text fontSize="12px" color={COR.apagado} mb={3}>
        (deduções + despesas tributárias + IRPJ/CSLL) ÷ receita bruta. IRPJ e CSLL são trimestrais
        no Presumido: no mês de fechamento do trimestre eles entram inteiros, então compare também a
        coluna do ano.
      </Text>
      <SimpleGrid columns={3} spacing={3} mb={4}>
        <Indicador
          rotulo="Carga do mês"
          valor={pct(carga.cargaMes)}
          sub={`${num(carga.totalMes)} sobre ${numCurto(carga.receitaMes)}`}
        />
        <Indicador
          rotulo="Carga do ano"
          valor={pct(carga.cargaAno)}
          sub={`${num(carga.totalAno)} sobre ${numCurto(carga.receitaAno)}`}
        />
        <Indicador
          rotulo="Tributos conferidos"
          valor={`${carga.conferidos} de ${carga.tributos.length}`}
          sub="apurado no Domínio igual ao contabilizado"
        />
      </SimpleGrid>
      <TableContainer whiteSpace="normal" mb={4}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Conta</Th>
              <Th isNumeric>No mês</Th>
              <Th isNumeric>% receita</Th>
              <Th isNumeric>No ano</Th>
            </Tr>
          </Thead>
          <Tbody>
            {carga.blocos.length === 0 && (
              <Tr>
                <Td colSpan={4} fontSize="12.5px" color={COR.apagado}>
                  Sem tributo lançado nesta competência.
                </Td>
              </Tr>
            )}
            {carga.blocos.map(b => (
              <React.Fragment key={b.linha}>
                <Tr bg="rgba(15,23,42,0.03)">
                  <Td fontSize="12.5px" fontWeight="700">
                    {b.nome}
                  </Td>
                  <Td isNumeric fontWeight="700">
                    {num(b.mes)}
                  </Td>
                  <Td isNumeric color={COR.apagado}>
                    {peso(b.mes)}
                  </Td>
                  <Td isNumeric fontWeight="700">
                    {num(b.ano)}
                  </Td>
                </Tr>
                {b.contas.map(c => (
                  <Tr key={`${b.linha}-${c.codigo}`}>
                    <Td fontSize="11.5px" pl={6}>
                      {textoDaContaOu(c.codigo, plano, c.nome)}
                    </Td>
                    <Td isNumeric fontSize="12px">
                      {num(Math.abs(c.mes))}
                    </Td>
                    <Td isNumeric fontSize="12px" color={COR.apagado}>
                      {peso(c.mes)}
                    </Td>
                    <Td isNumeric fontSize="12px">
                      {num(Math.abs(c.ano))}
                    </Td>
                  </Tr>
                ))}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {carga.tributos.length > 0 && (
        <>
          <Text fontSize="13px" fontWeight="700" color={COR.tinta}>
            Apurado × contabilizado × pago
          </Text>
          <Text fontSize="11.5px" color={COR.apagado} mb={2}>
            Cruzamento 3: o que o Domínio apurou, o que entrou na contabilidade e o que foi pago
            (e-CAC).
          </Text>
          <TableContainer whiteSpace="normal">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Tributo</Th>
                  <Th isNumeric>Apurado</Th>
                  <Th isNumeric>Contabilizado</Th>
                  <Th isNumeric>Pago</Th>
                  <Th isNumeric>Diferença</Th>
                </Tr>
              </Thead>
              <Tbody>
                {carga.tributos.map(t => {
                  const diferenca =
                    t.contabilizado == null ? null : (t.apurado ?? 0) - t.contabilizado;
                  return (
                    <Tr key={t.nome}>
                      <Td fontSize="12.5px">{t.nome}</Td>
                      <Td isNumeric fontSize="12px">
                        {num(t.apurado)}
                      </Td>
                      <Td isNumeric fontSize="12px">
                        {num(t.contabilizado)}
                      </Td>
                      <Td
                        isNumeric
                        fontSize="12px"
                        color={t.pago == null ? COR.apagado : undefined}
                      >
                        {t.pago == null ? 'sem dado' : num(t.pago)}
                      </Td>
                      <Td isNumeric fontSize="12px" color={diferenca ? COR.erro : COR.apagado}>
                        {num(diferenca)}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

/**
 * Janela do detalhe da carga tributária. As duas seções que ela usa (demonstrações e
 * cruzamentos) só são buscadas quando a janela abre.
 */
export function DetalheDaCarga({
  aberto,
  onFechar,
  empresaId,
  competencia,
  razao,
  dre,
  plano,
}: {
  aberto: boolean;
  onFechar: () => void;
  empresaId: number;
  competencia: string;
  razao: string;
  dre: { mes: Dre; ano: Dre };
  plano?: Record<string, IContaDoPlano>;
}) {
  const demonstracoes = useFechamentoSecao<IDemonstracoes>(
    'demonstracoes',
    empresaId,
    competencia,
    aberto,
  );
  const cruzamentos = useFechamentoSecao<ICruzamentos>(
    'cruzamentos',
    empresaId,
    competencia,
    aberto,
  );
  const d = demonstracoes.data?.dados;
  const c = cruzamentos.data?.dados;
  return (
    <Modal isOpen={aberto} onClose={onFechar} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">
          Carga tributária · {razao} · {mesLongo(competencia)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {d && c ? (
            <Conteudo dre={dre} demonstracoes={d} cruzamentos={c} plano={plano} />
          ) : (
            <Box>
              <Skeleton height="80px" mb={3} />
              <Skeleton height="200px" />
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onFechar}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
